import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { getStore, getFavorites, addFavoriteApi, removeFavoriteApi } from '../api';
import { SkinCard } from '../components/SkinCard';
import { CountdownTimer } from '../components/CountdownTimer';
import { Button, Disclaimer, PageHeader, Skeleton } from '../components/ui';
import { useApp } from '../stores/app';
import type { MeResponse } from '../api';
import { useWebSocket } from '../hooks/useWebSocket';
import { hapticNotif } from '../lib/telegram';

interface Props {
  me: MeResponse;
  onWantLink: () => void;
}

export function StorePage({ me, onWantLink }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { favorites, setFavorites, toggleFavoriteLocal } = useApp();

  const favQuery = useQuery({
    queryKey: ['favorites'],
    queryFn: getFavorites,
    enabled: me.hasRiotLink,
  });

  useEffect(() => {
    if (favQuery.data) {
      setFavorites(new Set(favQuery.data.map((f) => f.skinUuid)));
    }
  }, [favQuery.data, setFavorites]);

  const storeQuery = useQuery({
    queryKey: ['store'],
    queryFn: getStore,
    enabled: me.hasRiotLink,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // WebSocket: real-time updates
  useWebSocket((msg) => {
    if (msg.type === 'store_update') {
      qc.setQueryData(['store'], msg.payload);
    }
    if (msg.type === 'favorite_hit') {
      hapticNotif('success');
    }
    if (msg.type === 'auth_required') {
      qc.invalidateQueries({ queryKey: ['me'] });
    }
  });

  const storefront = storeQuery.data;
  const dailyOffers = storefront?.daily.offers ?? [];
  const hasFavHit = useMemo(
    () => dailyOffers.some((o) => favorites.has(o.offerId)),
    [dailyOffers, favorites],
  );

  const toggleFav = async (uuid: string) => {
    const isFav = favorites.has(uuid);
    toggleFavoriteLocal(uuid);
    try {
      if (isFav) await removeFavoriteApi(uuid);
      else await addFavoriteApi(uuid);
    } catch {
      toggleFavoriteLocal(uuid); // revert
    } finally {
      void qc.invalidateQueries({ queryKey: ['favorites'] });
    }
  };

  // Не подключён аккаунт
  if (!me.hasRiotLink) {
    return (
      <div className="pt-6">
        <PageHeader title={t('store.notLinkedTitle')} />
        <Disclaimer />
        <Button onClick={onWantLink} className="mt-6 w-full">
          {t('store.notLinkedCta')}
        </Button>
      </div>
    );
  }

  return (
    <div className="pt-6">
      <PageHeader title={t('store.title')} />

      {/* Countdown row */}
      <div className="mb-5 flex items-center justify-between clip-corners-sm border border-valo-navy-border bg-valo-navy-soft/50 px-4 py-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-valo-muted">
            {t('store.refreshIn')}
          </div>
          {storefront ? (
            <CountdownTimer
              remainingSeconds={storefront.daily.remainingSeconds}
              fetchedAt={storefront.fetchedAt}
              className="text-xl mt-0.5"
              onExpire={() => storeQuery.refetch()}
            />
          ) : (
            <Skeleton className="h-6 w-24 mt-1" />
          )}
        </div>
        <Button
          variant="ghost"
          onClick={() => storeQuery.refetch()}
          disabled={storeQuery.isFetching}
        >
          {storeQuery.isFetching ? '...' : t('store.refresh')}
        </Button>
      </div>

      {hasFavHit && (
        <div className="mb-5 clip-corners-sm border border-valo-red/50 bg-valo-red/10 p-3 animate-pulse-red">
          <div className="flex items-center gap-2 text-valo-red font-display tracking-wider uppercase">
            <span className="inline-block h-2 w-2 bg-valo-red rounded-full" />
            {t('store.favHit')}
          </div>
        </div>
      )}

      {/* Offers grid */}
      <div className="grid grid-cols-1 gap-3">
        {storeQuery.isLoading &&
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[16/9]" />
          ))}

        {dailyOffers.map((offer, i) =>
          offer.skin ? (
            <SkinCard
              key={offer.offerId}
              skin={offer.skin}
              price={offer.cost}
              isFavorite={favorites.has(offer.offerId)}
              onToggleFav={toggleFav}
              delayMs={i * 80}
            />
          ) : null,
        )}

        {storeQuery.isError && (
          <div className="clip-corners-sm border border-valo-red/40 bg-valo-red/10 p-4 text-sm text-valo-red">
            {(storeQuery.error as Error)?.message ?? 'Error'}
            <Button
              variant="ghost"
              className="mt-3"
              onClick={() => storeQuery.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        )}
      </div>

      {/* Night Market */}
      {storefront?.bonus && storefront.bonus.offers.length > 0 && (
        <section className="mt-8">
          <h2 className="font-display text-2xl uppercase text-valo-gold mb-3 flex items-center gap-2">
            <span className="h-3 w-3 bg-valo-gold rounded-full animate-pulse" />
            {t('store.nightMarket')}
          </h2>
          <div className="mb-3 text-xs text-valo-muted">
            <CountdownTimer
              remainingSeconds={storefront.bonus.remainingSeconds}
              fetchedAt={storefront.fetchedAt}
            />
          </div>
          <div className="grid grid-cols-1 gap-3">
            {storefront.bonus.offers.map(
              (o, i) =>
                o.skin && (
                  <SkinCard
                    key={o.offerId}
                    skin={o.skin}
                    price={o.discountedCost}
                    originalPrice={o.cost}
                    discountPercent={o.discountPercent}
                    isFavorite={favorites.has(o.offerId)}
                    onToggleFav={toggleFav}
                    delayMs={i * 80}
                  />
                ),
            )}
          </div>
        </section>
      )}
    </div>
  );
}
